import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

import API_BASE_URL from "../../../../Constants";

export default function CreateAccountPricingModal({
  open,
  onClose,
  refreshList,
  currentLevel, // We still accept the current "level" from the parent
}) {
  // Track whether we are in "expanded" mode
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => setIsExpanded((prev) => !prev);

  // 1) Agency
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState("");

  // 2) Agency Tests
  const [agencyTests, setAgencyTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState("");

  // 3) Account Level
  const [level, setLevel] = useState(currentLevel || "BUILDING");

  // 4) The list of accounts for that level
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  // 5) Additional fields for main `account_pricing`
  const [formData, setFormData] = useState({
    dateFrom: "",
    dateTo: "",
    amount: "",
    active: true,
  });

  // ---------------------------
  // Fetch Logic
  // ---------------------------
  useEffect(() => {
    if (open) {
      fetchAllAgencies();
      fetchAccountsForLevel(level);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchAccountsForLevel(level);
      setSelectedAccountId("");
    }
  }, [level, open]);

  useEffect(() => {
    if (selectedAgencyId) {
      fetchAgencyTests(selectedAgencyId);
      setSelectedTestId("");
    } else {
      setAgencyTests([]);
      setSelectedTestId("");
    }
  }, [selectedAgencyId]);

  const fetchAllAgencies = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/inspections/agency/agencies`,
        { credentials: "include" }
      );
      if (!res.ok) {
        throw new Error(`Agency fetch error. Status ${res.status}`);
      }
      const data = await res.json();
      setAgencies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching agencies:", err);
      setAgencies([]);
    }
  };

  const fetchAgencyTests = async (agencyId) => {
    try {
      const url = `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${agencyId}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        throw new Error(`Agency tests fetch error. Status ${res.status}`);
      }
      const data = await res.json();
      setAgencyTests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching agency tests:", err);
      setAgencyTests([]);
    }
  };

  const fetchAccountsForLevel = async (lvl) => {
    try {
      let url = null;
      switch (lvl) {
        case "BUILDING":
          url = `${API_BASE_URL}/api/setup/buildings/all`;
          break;
        case "CUSTOMER":
          url = `${API_BASE_URL}/api/setup/customers/all`;
          break;
        case "MGMTCO":
          url = `${API_BASE_URL}/api/setup/managementcompanies/all`;
          break;
        case "SERVICECO":
          url = `${API_BASE_URL}/api/inspections/service-companies`;
          break;
        default:
          url = null;
      }
      if (!url) return;

      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        throw new Error(`Fetch for ${lvl} error. Status ${res.status}`);
      }
      const data = await res.json();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(`Error fetching accounts for level ${lvl}:`, err);
      setAccounts([]);
    }
  };

  // ---------------------------
  // Handlers
  // ---------------------------
  const handleClose = () => onClose();

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const handleSave = async () => {
    // Basic validations
    if (!selectedAgencyId) {
      alert("Please select an Agency");
      return;
    }
    if (!selectedTestId) {
      alert("Please select a Test");
      return;
    }
    if (!selectedAccountId) {
      alert(`Please pick an account from level: ${level}`);
      return;
    }

    try {
      // 1) POST main row
      const chosenAcct = accounts.find((acct) => {
        return (
          acct.buildingID?.toString() === selectedAccountId ||
          acct.customerID?.toString() === selectedAccountId ||
          acct.managementId?.toString() === selectedAccountId ||
          acct.serviceCompanyId?.toString() === selectedAccountId
        );
      });

      const accountLabel =
        chosenAcct?.buildingReference ||
        chosenAcct?.customerName ||
        chosenAcct?.companyName ||
        `ID #${selectedAccountId}`;

      const mainPayload = {
        agencyId: parseInt(selectedAgencyId, 10),
        testId: parseInt(selectedTestId, 10),
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        amount: parseFloat(formData.amount || "0"),
        active: !!formData.active,
        account: accountLabel,
      };

      const createRes = await fetch(
        `${API_BASE_URL}/api/inspections/account-pricing`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(mainPayload),
        }
      );
      if (!createRes.ok) {
        throw new Error(
          `Failed to create main row. HTTP status = ${createRes.status}`
        );
      }
      const createdData = await createRes.json();
      const newId = createdData.accountPricingId;

      // 2) POST intersection row
      let intersectionUrl = "";
      const intersectionPayload = { accountPricingId: newId };

      switch (level) {
        case "BUILDING":
          intersectionUrl = `${API_BASE_URL}/api/inspections/account-pricing-building`;
          intersectionPayload.buildingId = parseInt(selectedAccountId, 10);
          break;
        case "CUSTOMER":
          intersectionUrl = `${API_BASE_URL}/api/inspections/account-pricing-customer`;
          intersectionPayload.customerId = parseInt(selectedAccountId, 10);
          break;
        case "MGMTCO":
          intersectionUrl = `${API_BASE_URL}/api/inspections/account-pricing-management`;
          intersectionPayload.managementId = parseInt(selectedAccountId, 10);
          break;
        case "SERVICECO":
          intersectionUrl = `${API_BASE_URL}/api/inspections/account-pricing-serviceco`;
          intersectionPayload.serviceCompanyId = parseInt(selectedAccountId, 10);
          break;
        default:
          intersectionUrl = "";
      }

      if (intersectionUrl) {
        const intRes = await fetch(intersectionUrl, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(intersectionPayload),
        });
        if (!intRes.ok) {
          const text = await intRes.text();
          throw new Error(
            `Failed to create intersection row. Status = ${intRes.status}. Body: ${text}`
          );
        }
      }

      alert(`Created new account pricing #${newId}.`);
      if (refreshList) refreshList();
      onClose();
    } catch (err) {
      console.error("Error creating AccountPricing:", err);
      alert(`Error: ${err.message}`);
    }
  };

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      // Let's allow custom sizing so that "expand" can actually change the width
      maxWidth={false}
      fullWidth
      PaperProps={{
        style: {
          // If expanded, 90% of the screen width; else a large but fixed width
          width: isExpanded ? "90%" : "1000px",
          maxHeight: "90vh",
          minHeight: "90vh",
        },
      }}
      style={{ zIndex: 1400 }}
    >
      {/* Title & Expand Button */}
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="text-xl font-bold">Add Account Pricing</h2>

        <button
          onClick={toggleExpand}
          className="p-2 rounded-full hover:bg-gray-200 transition"
        >
          {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </button>
      </div>

      {/* Submit Button Under the Title Line */}
      <div className="px-4 pt-3">
        <button
          onClick={handleSave}
          className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-4 py-2 rounded"
        >
          Submit
        </button>
      </div>

      <DialogContent>
        <div className="space-y-6 mt-3">
          {/* ~~~~~~~~~~~ Section: Agency & Test ~~~~~~~~~~~ */}
          <div className="bg-gray-50 p-4 rounded-md shadow-sm">
            <h3 className="text-lg font-semibold mb-2">Agency & Test</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Agency */}
              <div>
                <label className="block font-semibold mb-1">Agency:</label>
                <select
                  className="border p-2 w-full"
                  value={selectedAgencyId}
                  onChange={(e) => setSelectedAgencyId(e.target.value)}
                >
                  <option value="">--Select Agency--</option>
                  {agencies.map((ag) => (
                    <option key={ag.agencyId} value={ag.agencyId}>
                      {ag.agencyName} (#{ag.agencyId})
                    </option>
                  ))}
                </select>
              </div>

              {/* Test */}
              <div>
                <label className="block font-semibold mb-1">Test:</label>
                <select
                  className="border p-2 w-full"
                  value={selectedTestId}
                  onChange={(e) => setSelectedTestId(e.target.value)}
                >
                  <option value="">--Select Test--</option>
                  {agencyTests.map((t) => (
                    <option key={t.testId} value={t.testId}>
                      {t.label} (#{t.testId})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ~~~~~~~~~~~ Section: Account Selection ~~~~~~~~~~~ */}
          <div className="bg-gray-50 p-4 rounded-md shadow-sm">
            <h3 className="text-lg font-semibold mb-2">Account Selection</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Level */}
              <div>
                <label className="block font-semibold mb-1">Account Level:</label>
                <select
                  className="border p-2 w-full"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                >
                  <option value="BUILDING">Building</option>
                  <option value="CUSTOMER">Customer</option>
                  <option value="MGMTCO">Management</option>
                  <option value="SERVICECO">Service Co</option>
                </select>
              </div>

              {/* Specific Account */}
              <div>
                <label className="block font-semibold mb-1">Account:</label>
                <select
                  className="border p-2 w-full"
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                >
                  <option value="">--Select {level}--</option>
                  {accounts.map((acct) => {
                    const label =
                      acct.buildingReference ||
                      acct.customerName ||
                      acct.companyName ||
                      `ID #${
                        acct.buildingID ||
                        acct.customerID ||
                        acct.managementId ||
                        acct.serviceCompanyId
                      }`;

                    const val =
                      acct.buildingID ||
                      acct.customerID ||
                      acct.managementId ||
                      acct.serviceCompanyId;

                    return (
                      <option key={val} value={val}>
                        {label} (#{val})
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>

          {/* ~~~~~~~~~~~ Section: Dates & Amount ~~~~~~~~~~~ */}
          <div className="bg-gray-50 p-4 rounded-md shadow-sm">
            <h3 className="text-lg font-semibold mb-2">Dates & Amount</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date From */}
              <div>
                <label className="block font-semibold mb-1">Date From:</label>
                <input
                  type="date"
                  name="dateFrom"
                  className="border p-2 w-full"
                  value={formData.dateFrom}
                  onChange={handleFormChange}
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block font-semibold mb-1">Date To:</label>
                <input
                  type="date"
                  name="dateTo"
                  className="border p-2 w-full"
                  value={formData.dateTo}
                  onChange={handleFormChange}
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block font-semibold mb-1">Amount:</label>
                <input
                  type="number"
                  step="0.01"
                  name="amount"
                  className="border p-2 w-full"
                  value={formData.amount}
                  onChange={handleFormChange}
                />
              </div>

              {/* Active checkbox */}
              <div className="flex items-center space-x-3 mt-6">
                <label className="font-semibold">Active?</label>
                <input
                  type="checkbox"
                  name="active"
                  checked={!!formData.active}
                  onChange={handleFormChange}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
